/**
 * Vault context — the bridge between lib/vault.js (imperative, framework-free)
 * and React. The vault emits on every change; this re-reads the derived
 * collections and hands them to the tree.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as vault from "../lib/vault.js";

const VaultContext = createContext(null);

const IDLE_LOCK_MS = 10 * 60 * 1000;

export function VaultProvider({ children }) {
  const [unlocked, setUnlocked] = useState(false);
  const [tick, setTick] = useState(0);
  // Set while the editor is open, so a long writing session with pauses is
  // never auto-locked out from under the user.
  const [lockSuspended, setLockSuspended] = useState(false);

  // Any vault mutation bumps the tick and the derived values below recompute.
  useEffect(() => vault.subscribe(() => setTick((t) => t + 1)), []);

  const state = useMemo(
    () => ({
      entries: unlocked ? vault.entries() : [],
      transactions: unlocked ? vault.transactions() : [],
      accounts: unlocked ? vault.accounts() : [],
      goals: unlocked ? vault.goals() : [],
      preferences: unlocked ? vault.setting(vault.SETTING_IDS.preferences, {}) : {},
      categoryRules: unlocked ? vault.setting(vault.SETTING_IDS.categoryRules, {}) : {},
      categories: unlocked ? vault.setting(vault.SETTING_IDS.categories, null) : null,
      // Named syncState, not sync: `sync` is the action exposed below.
      syncState: vault.getSyncState(),
    }),
    // `tick` is the dependency that matters — it changes on every vault emit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tick, unlocked]
  );

  // --- lifecycle actions --------------------------------------------------
  const create = useCallback(async (password, registrationToken = "") => {
    await vault.createVault(password, registrationToken);
    setUnlocked(true);
  }, []);

  const unlock = useCallback(async (password) => {
    const ok = await vault.unlockVault(password);
    if (ok) {
      setUnlocked(true);
      vault.sync().catch(() => {});
    }
    return ok;
  }, []);

  const join = useCallback(async (args) => {
    await vault.joinVault(args);
    setUnlocked(true);
  }, []);

  const lock = useCallback(() => {
    vault.lockVault();
    setUnlocked(false);
  }, []);

  const reset = useCallback(() => {
    vault.resetVault();
    setUnlocked(false);
  }, []);

  // --- idle auto-lock ------------------------------------------------------
  useEffect(() => {
    if (!unlocked || lockSuspended) return;
    let timer = setTimeout(lock, IDLE_LOCK_MS);
    const bump = () => {
      clearTimeout(timer);
      timer = setTimeout(lock, IDLE_LOCK_MS);
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, bump));
    };
  }, [unlocked, lockSuspended, lock]);

  // Keep other tabs and devices in step while the app is open and visible.
  useEffect(() => {
    if (!unlocked) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") vault.sync().catch(() => {});
    };
    const id = setInterval(() => vault.sync().catch(() => {}), 60_000);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [unlocked]);

  const value = useMemo(
    () => ({
      ...state,
      unlocked,
      hasVault: vault.hasVault(),
      meta: vault.loadMeta(),
      create,
      unlock,
      join,
      lock,
      reset,
      setLockSuspended,
      // pass-throughs
      put: vault.put,
      remove: vault.remove,
      patchSetting: vault.patchSetting,
      setting: vault.setting,
      SETTING_IDS: vault.SETTING_IDS,
      verifyPassword: vault.verifyPassword,
      sync: vault.sync,
      exportBackup: vault.exportBackup,
      importBackup: vault.importBackup,
      setSyncCredentials: vault.setSyncCredentials,
      getNotified: vault.getNotified,
      markNotified: vault.markNotified,
    }),
    [state, unlocked, create, unlock, join, lock, reset]
  );

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
}

export function useVault() {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error("useVault must be used inside <VaultProvider>");
  return ctx;
}
